import { createGlobalStyle, css } from "styled-components";
import { colors, darkModeColors, fonts } from "../../consts";
import { Paragraph2 } from "../Text/Paragraphs";
import { DateInputCss } from "../DateInput/DateInputCss";
import { TimeInputCss } from "../TimeInput/TimeInputCss";

const Portal = css`
  position: absolute;
  z-index: 1000;
`;

const GlobalStyle = createGlobalStyle`
  #date-input-portal {
    ${Portal}
    ${DateInputCss}
  }

  #time-input-portal {
    ${Portal}
    ${TimeInputCss}
  }

  #dropdown-portal {
    ${Portal}
  }


  html,
  body {
    margin: 0;
    background: ${darkModeColors.ISABELLINE_DARK_RAISIN};
    user-select: none;
    box-sizing: border-box;
    overflow: visible;
  }

  iframe {
    pointer-events: none;
  }

  * {
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
    box-sizing: border-box;
  }

  #app {
    width: auto;
    min-height: 100vh;
    height: auto;
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    font-family: ${fonts.PLUS_JAKARTA_SANS};
    letter-spacing: 0;
  }

  table{
    border-collapse: collapse;
  }

  header {
        z-index: 1;
        height: 56px;
        width: 100%;
        
        background: ${darkModeColors.WHITE_RAISIN_100};

        position: relative;
        top: 0;
        left: 0;
        box-sizing: border-box;
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0 44px;

        box-shadow: 0px 2px 12px rgba(0, 0, 0, 0.08);
    }

    footer {
      display: flex;
      justify-content: space-between;
      padding: 0 32px;

      ${Paragraph2} {
        font-size: 12px;
        line-height: 18px;
        color: ${colors.RAISIN_50};
      }
    }

    main {
        box-sizing: border-box;
        display: flex;
        width: 100%;
        height: 100%;
        min-height: ${(props) => (props?.cognitoUser ? "100vh" : "calc(100vh - 56px)")};
    }

    input::-ms-reveal,
    input::-ms-clear {
      display: none;
    }

    input, textarea{
      box-sizing: border-box;
      font-family: ${fonts.PLUS_JAKARTA_SANS};
    }

    input[type="checkbox"] {
      clip: rect(0 0 0 0);
      clip-path: inset(50%);
      height: 1px;
      overflow: hidden;
      position: absolute;
      white-space: nowrap;
      width: 1px;
    }

    button {
      padding: unset;
      outline: none;
      border: none;
      background: none;
      cursor: pointer;

      &:hover{
        opacity: 0.6;
      }
    }

    button, a{
      transition: 0.5s all;
      font-family: ${fonts.PLUS_JAKARTA_SANS};
    }

    a{
      text-decoration: none;

      &:hover{
        opacity: 0.6;
      }
    }

    h1,
    h2,
    h3,
    h4,
    h5,
    h6,
    p, 
    li,
    th,
    td,
    label {
      margin: 0;
      padding: 0;
      color: ${darkModeColors.RAISIN_100_ISABELLINE};
      transition: color 0.8s ease;
      font-weight: 800;
      font-family: ${fonts.PLUS_JAKARTA_SANS};
    }

    p, li, label, td {
      font-weight: 400;
      color: ${darkModeColors.RAISIN_100_RAISIN_20};
    }

    td{
      font-size: 16px;
      line-height: 26px;
      color: ${darkModeColors.RAISIN_70_RAISIN_40};

      p, div{
        transition: color 0s ease;
      }

      span{
        font-weight: 700;
      }
    }

    th{
      font-weight: 700;
      color: ${darkModeColors.RAISIN_80_RAISIN_20};

      font-size: 14px;
      line-height: 26px;
      text-align: left;
    }

    ul {
      list-style: none;
      text-align: left;

      margin-block-start: 0;
      margin-block-end: 0;
      margin-inline-start: 0;
      margin-inline-end: 0;
      padding-inline-start: 0;
    }

    .disabled {
      pointer-events: none;
      cursor: default;
    }

    ::-webkit-scrollbar {
      border-radius: 8px;
      width: 4px;
      height: 4px;
    }

    ::-webkit-scrollbar-thumb {
      background: ${colors.RAISIN_40};
      border-radius: 8px;
    }

    // react-easy-sort
    .dragged-page{
      cursor: move;

      background: ${colors.RAISIN_20};
      font-weight: 700;
      opacity: 1;

      &:before{
        content: url("data:image/svg+xml,%3Csvg width='7' height='11' viewBox='0 0 7 11' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1.07683 10.1537C0.780722 10.1537 0.527222 10.0482 0.316333 9.83733C0.105444 9.62644 0 9.37294 0 9.07683C0 8.78072 0.105444 8.52722 0.316333 8.31633C0.527222 8.10544 0.780722 8 1.07683 8C1.37294 8 1.62644 8.10544 1.83733 8.31633C2.04822 8.52722 2.15367 8.78072 2.15367 9.07683C2.15367 9.37294 2.04822 9.62644 1.83733 9.83733C1.62644 10.0482 1.37294 10.1537 1.07683 10.1537ZM5.07683 10.1537C4.78072 10.1537 4.52722 10.0482 4.31633 9.83733C4.10544 9.62644 4 9.37294 4 9.07683C4 8.78072 4.10544 8.52722 4.31633 8.31633C4.52722 8.10544 4.78072 8 5.07683 8C5.37294 8 5.62644 8.10544 5.83733 8.31633C6.04822 8.52722 6.15367 8.78072 6.15367 9.07683C6.15367 9.37294 6.04822 9.62644 5.83733 9.83733C5.62644 10.0482 5.37294 10.1537 5.07683 10.1537ZM1.07683 6.15367C0.780722 6.15367 0.527222 6.04822 0.316333 5.83733C0.105444 5.62644 0 5.37294 0 5.07683C0 4.78072 0.105444 4.52722 0.316333 4.31633C0.527222 4.10544 0.780722 4 1.07683 4C1.37294 4 1.62644 4.10544 1.83733 4.31633C2.04822 4.52722 2.15367 4.78072 2.15367 5.07683C2.15367 5.37294 2.04822 5.62644 1.83733 5.83733C1.62644 6.04822 1.37294 6.15367 1.07683 6.15367ZM5.07683 6.15367C4.78072 6.15367 4.52722 6.04822 4.31633 5.83733C4.10544 5.62644 4 5.37294 4 5.07683C4 4.78072 4.10544 4.52722 4.31633 4.31633C4.52722 4.10544 4.78072 4 5.07683 4C5.37294 4 5.62644 4.10544 5.83733 4.31633C6.04822 4.52722 6.15367 4.78072 6.15367 5.07683C6.15367 5.37294 6.04822 5.62644 5.83733 5.83733C5.62644 6.04822 5.37294 6.15367 5.07683 6.15367ZM1.07683 2.15367C0.780722 2.15367 0.527222 2.04822 0.316333 1.83733C0.105444 1.62644 0 1.37294 0 1.07683C0 0.780722 0.105444 0.527222 0.316333 0.316333C0.527222 0.105444 0.780722 0 1.07683 0C1.37294 0 1.62644 0.105444 1.83733 0.316333C2.04822 0.527222 2.15367 0.780722 2.15367 1.07683C2.15367 1.37294 2.04822 1.62644 1.83733 1.83733C1.62644 2.04822 1.37294 2.15367 1.07683 2.15367ZM5.07683 2.15367C4.78072 2.15367 4.52722 2.04822 4.31633 1.83733C4.10544 1.62644 4 1.37294 4 1.07683C4 0.780722 4.10544 0.527222 4.31633 0.316333C4.52722 0.105444 4.78072 0 5.07683 0C5.37294 0 5.62644 0.105444 5.83733 0.316333C6.04822 0.527222 6.15367 0.780722 6.15367 1.07683C6.15367 1.37294 6.04822 1.62644 5.83733 1.83733C5.62644 2.04822 5.37294 2.15367 5.07683 2.15367Z' fill='%2334303D' /%3E%3C/svg%3E");
        width: 10px;
        height: 100%;
        background: ${colors.RAISIN_10};
        position: absolute;
        left: 0;

        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
      }
    }

    .slick-slider {
      width: 100%;
    }

  /* animations */
  @keyframes enter {
    from {
      transform: translateY(calc(-100vh));
    }
    to {  
      transform: translateY(calc(0%));
    }
  }

  .animate-enter{
    animation-name: enter;
    animation-duration: .5s;
  }

  .animate-leave{
    transform: translateY(calc(-100vh));
    transition: .5s;
  }

  @keyframes growDown {
    0% {
        transform: scaleY(0)
    }
    80% {
        transform: scaleY(1.1)
    }
    100% {
        transform: scaleY(1)
    }
  }

  @keyframes growUp {
    0% {
        transform: scaleY(1)
    }
    20% {
        transform: scaleY(1.1)
    }
    100% {
        transform: scaleY(0)
    }
  }

  @keyframes expandRight{
    0% {
        transform: scaleX(0)
    }
    100% {
        transform: scaleX(1)
    }
  }

  @keyframes shrinkLeft {
    0% {
        transform: scaleX(1)
    }
    100% {
        transform: scaleX(0)
    }
  }

  @keyframes spin {
    from {
        transform:rotate(360deg);
    }
    to {
        transform:rotate(0deg);
    }
  }
`;

export { GlobalStyle };
